import { useState, useEffect } from "react";
import "../../assets/css/Renewal/RenewalDrawer.css";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { MdClose, MdOutlineNotes, MdAutorenew } from "react-icons/md";
import { FaEdit, FaTrashAlt, FaBuilding, FaHandshake, FaCalendarAlt, FaUser } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import LeadsCreate from "../Leads/LeadsCreate";

export default function RenewalDrawer({
  renewal,
  isOpen,
  onClose,
  refreshRenewals,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [openAddLead, setOpenAddLead] = useState(false);

  useEffect(() => {
    if (!renewal?.id) return;

    setFormData({
      id: renewal.id,
      amount: renewal.amount ?? "",
      notes: renewal.notes ?? "",
      next_renewal_date: renewal.next_renewal_date ?? "",

      // ORGANIZATION (safe)
      organizationName: renewal.organization?.name || "",
      organizationWebsite: renewal.organization?.website || "",
      organizationAddress: renewal.organization?.address || "",

      // DEAL / LEAD FIX (IMPORTANT)
      dealTitle: renewal.deal?.title || renewal.lead?.title || "",

      dealAmount:
        renewal.deal?.deal_amount ??
        renewal.deal?.amount ??
        renewal.lead?.value ??
        0,

      dealStatus:
        renewal.deal?.status ||
        renewal.lead?.status ||
        "",

      expectedCloseDate: renewal.deal?.expected_close_date || "",

      // ASSIGNED USER
      assignedTo: renewal.assigned_to?.name || "",
    });

    setEditMode(false);
    setActiveTab("overview");
  }, [renewal]);

  const [errors, setErrors] = useState({
    amount: "",
    next_renewal_date: "",
    notes: "",
  });

  const validateForm = () => {
    const newErrors = {};

    if (formData.amount === "" || formData.amount === null || formData.amount === undefined) {
      newErrors.amount = "Renewal amount is required";
    } else if (Number(formData.amount) < 0) {
      newErrors.amount = "Amount cannot be negative";
    }

    if (!formData.next_renewal_date) {
      newErrors.next_renewal_date = "Next renewal date is required";
    } else {
      const selectedDate = new Date(formData.next_renewal_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.next_renewal_date = "Date cannot be in the past";
      }
    }

    if (formData.notes && formData.notes.length > 1000) {
      newErrors.notes = "Notes cannot exceed 1000 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const saveRenewal = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        amount: formData.amount,
        notes: formData.notes,
        next_renewal_date: formData.next_renewal_date,
      };

      await api.put(`/renewal/${renewal.id}`, payload);

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        timer: 1500,
        showConfirmButton: false,
      });

      await refreshRenewals?.();
      setEditMode(false);
      onClose();
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
      });
    }
  };

  const deleteRenewal = async () => {
    const result = await Swal.fire({
      title: "Delete Renewal?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/renewal/${renewal.id}`);

      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        timer: 1500,
        showConfirmButton: false,
      });

      await refreshRenewals?.();
      onClose();
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
      });
    }
  };

  const currentUser = JSON.parse(sessionStorage.getItem("user"));
  const role = currentUser?.role?.toLowerCase();

  const permissions = {
    "super admin": { edit: true, delete: true },
    "sales manager": { edit: true, delete: false },
    "sales executive": { edit: false, delete: false },
  };

  const permission = permissions[role] || { edit: false, delete: false };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="renewal-drawer" onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="drawer-header-r">
          <div className="renewal-header-info">
            <div className="renewal-avatar">
              {formData.organizationName
                ?.split(" ")
                .slice(0, 2)
                .map((word) => word[0])
                .join("")
                .toUpperCase() || "RN"}
            </div>

            <div className="renewal-header-content">
              <h2>{formData.organizationName || "Renewal"}</h2>
              <div className="renewal-badges">
                <span className="renewal-status-badge">
                  {formData.dealStatus || "Unknown"}
                </span>
                <span className="renewal-amount-badge">
                  ₹{Number(formData.amount || 0).toLocaleString('en-IN')}
                </span>
              </div>

              {formData.organizationWebsite && (
                <a
                  href={
                    formData.organizationWebsite.startsWith("http")
                      ? formData.organizationWebsite
                      : `https://${formData.organizationWebsite}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="renewal-website"
                >
                  {formData.organizationWebsite}
                </a>
              )}
            </div>
          </div>

          <div className="renewal-header-actions">
            {!editMode && renewal?.auto_lead_id && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "#ecfdf5",
                  color: "#065f46",
                  border: "1px solid #a7f3d0"
                }}
                title="Lead automatically generated for this renewal"
              >
                ✓ Auto Lead: {renewal.auto_lead?.lead_code || `#${renewal.auto_lead_id}`}
              </span>
            )}

            {!editMode && !renewal?.auto_lead_id && (
              <button
                type="button"
                className="btn-drawer-add-lead"
                onClick={() => setOpenAddLead(true)}
                title="Add Lead for this Renewal"
              >
                <FiPlus size={14} /> Add Lead
              </button>
            )}

            {!editMode && (
              <div className="header-icon-actions">
                {permission.edit && (
                  <button
                    className="icon-btn edit-btn"
                    onClick={() => setEditMode(true)}
                    title="Edit Renewal"
                  >
                    <FaEdit />
                  </button>
                )}

                {permission.delete && (
                  <button
                    className="icon-btn delete-btn"
                    onClick={deleteRenewal}
                    title="Delete Renewal"
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </div>
            )}

            <button className="icon-btn close-btn" onClick={onClose} title="Close">
              <MdClose />
            </button>
          </div>
        </div>

        {/* HERO SUMMARY BANNER */}
        {!editMode && (
          <div className="renewal-hero-banner">
            <div className="hero-stat-card primary">
              <span className="hero-stat-lbl">Renewal Amount</span>
              <span className="hero-stat-val">₹{Number(formData.amount || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="hero-stat-card danger">
              <span className="hero-stat-lbl">Next Renewal Date</span>
              <span className="hero-stat-val">{formData.next_renewal_date || "Not Set"}</span>
            </div>
            <div className="hero-stat-card">
              <span className="hero-stat-lbl">Assigned Rep</span>
              <span className="hero-stat-val">{formData.assignedTo || "Unassigned"}</span>
            </div>
          </div>
        )}

        {/* CONTENT */}
        <div className="drawer-content">
          {editMode ? (
            <div className="renewal-edit-form">
              <h3 className="section-title">Edit Renewal Details</h3>
              <div className="form-group">
                <label>Renewal Amount (₹)</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  className={errors.amount ? "error" : ""}
                />
                {errors.amount && <span className="error-text">{errors.amount}</span>}
              </div>

              <div className="form-group">
                <label>Next Renewal Date</label>
                <input
                  type="date"
                  name="next_renewal_date"
                  value={formData.next_renewal_date}
                  onChange={handleChange}
                  className={errors.next_renewal_date ? "error" : ""}
                />
                {errors.next_renewal_date && <span className="error-text">{errors.next_renewal_date}</span>}
              </div>

              <div className="form-group full-width">
                <label>Notes & Requirements</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Enter any renewal notes..."
                  className={errors.notes ? "error" : ""}
                />
                {errors.notes && <span className="error-text">{errors.notes}</span>}
              </div>

              <div className="actions">
                <button type="button" className="btn-cancel" onClick={() => setEditMode(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-save" onClick={saveRenewal}>
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* RENEWAL DETAILS SECTION */}
              <div className="renewal-section">
                <h4 className="section-title"><MdAutorenew style={{ marginRight: '6px', verticalAlign: 'middle' }} />Renewal Information</h4>
                <div className="section-card">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Renewal Amount</label>
                      <p className="highlight-val">₹{Number(formData.amount || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="info-item">
                      <label><FaCalendarAlt style={{ marginRight: '4px' }} />Next Renewal Date</label>
                      <p>{formData.next_renewal_date || "-"}</p>
                    </div>
                    {renewal?.auto_lead_id && (
                      <div className="info-item">
                        <label>Auto-Generated Lead</label>
                        <p style={{ color: "#059669", fontWeight: 600 }}>
                          {renewal.auto_lead?.title || `Lead #${renewal.auto_lead_id}`}
                          {renewal.auto_lead?.status ? ` (${renewal.auto_lead.status})` : ""}
                        </p>
                      </div>
                    )}
                    <div className="info-item full-width">
                      <label><MdOutlineNotes style={{ marginRight: '4px' }} />Notes</label>
                      <p className="notes-box">{formData.notes || "No notes available for this renewal."}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ORGANIZATION DETAILS SECTION */}
              <div className="renewal-section">
                <h4 className="section-title"><FaBuilding style={{ marginRight: '6px', verticalAlign: 'middle' }} />Organization Details</h4>
                <div className="section-card">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Company Name</label>
                      <p>{formData.organizationName || "-"}</p>
                    </div>
                    <div className="info-item">
                      <label>Website</label>
                      <p>{formData.organizationWebsite || "-"}</p>
                    </div>
                    <div className="info-item full-width">
                      <label>Address</label>
                      <p>{formData.organizationAddress || "-"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* DEAL & LEAD DETAILS SECTION */}
              <div className="renewal-section">
                <h4 className="section-title"><FaHandshake style={{ marginRight: '6px', verticalAlign: 'middle' }} />Associated Deal / Lead</h4>
                <div className="section-card">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Deal Title</label>
                      <p>{formData.dealTitle || "-"}</p>
                    </div>
                    <div className="info-item">
                      <label>Original Deal Amount</label>
                      <p>₹{Number(formData.dealAmount || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="info-item">
                      <label>Deal Status</label>
                      <p><span className="renewal-status-badge">{formData.dealStatus || "Active"}</span></p>
                    </div>
                    <div className="info-item">
                      <label><FaUser style={{ marginRight: '4px' }} />Assigned Representative</label>
                      <p>{formData.assignedTo || "Unassigned"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {openAddLead && (
        <LeadsCreate
          onClose={() => setOpenAddLead(false)}
          onSuccess={() => {
            setOpenAddLead(false);
            refreshRenewals?.();
          }}
          initialData={{
            organization_id:
              renewal?.organization?.id ||
              renewal?.organization_id ||
              "",
            title: renewal?.organization?.name || "",
            value: renewal?.amount || "",
            contact_id:
              renewal?.contact?.id ||
              renewal?.contact_id ||
              renewal?.lead?.contact?.id ||
              renewal?.lead?.contact_id ||
              renewal?.deal?.contact?.id ||
              renewal?.deal?.contact_id ||
              renewal?.organization?.contact?.id ||
              renewal?.organization?.contact_id ||
              "",
            assigned_to:
              currentUser?.id ||
              currentUser?.user_id ||
              "",
          }}
        />
      )}
    </div>
  );
}